import { CityDTO, StudentCountryDTO } from '@/generated/@habacus/hcp_api/src';
import { SelectData } from '@/types/typesInterfaces';
import { fetchCities, fetchCountries } from '@/utils/client/client_api';
import { calculateCodiceFiscale } from '@/utils/codicefiscale';
import { selectOnSeach } from '@/utils/helpers';
import { isNullOrEmpty } from '@/utils/string_utils';
import { validationNumber, ValidationResult } from '@/utils/validation_utils';
import { Button, Input, Label, RadioButton, REGEX_TEXT_ONLY_CHAR, Select, TitleHeading } from '@habacuscode/habacus_ui_component_library';
import { useTranslations } from 'next-intl';
import React, { FormEvent, useEffect, useState } from 'react';

// Types
type Gender = 'M' | 'F';

interface FiscalCodeModalProps {
  locale: string,
  data?: FiscalCodeData
  onClose: () => void;
  onChangeData: (e: any, name: string) => void;
  onConfirm: (fiscalCode: string) => void;
}

export const fiscalCodeDataName = {
  firstName: "name",
  lastName: "surname",
  gender: "gender",
  birthDate: {
    day: "day",
    month: "month",
    year: "year"
  },
  birthPlace: {
    country: "country",
    place: "place"
  }
}

export interface FiscalCodeData {
  firstName: string;
  lastName: string;
  gender: Gender;
  birthDate: {
    day: string;
    month: string;
    year: string;
  };
  birthPlace: {
    country: string;
    place: string;
  };
}

// Italian Codice Fiscale Calculator Component
const FiscalCodeModal: React.FC<FiscalCodeModalProps> = ({ locale, data, onChangeData, onClose, onConfirm }) => {
  const tInput = useTranslations('Input');
  const tCta = useTranslations('Cta');
  const tCommon = useTranslations('Common');
  const tFiscalCode = useTranslations('FiscalCode');

  const [loading, setLoading] = useState(false);
  const [fiscalCode, setFiscalCode] = useState<string>('');
  const [showResult, setShowResult] = useState<boolean>(false);

  const [firstName, setFirstName] = useState<string>(data?.firstName ?? '');
  const [lastName, setLastName] = useState<string>(data?.lastName ?? '');

  const [genders, setGenders] = useState<Array<SelectData>>([
    { value: "M", label: tCommon('male') },
    { value: "F", label: tCommon('female') }
  ]);
  const [selectedGender, setSelectedGender] = useState<SelectData | undefined>(genders.find(
    (gender: SelectData) => gender.value == (data?.gender ?? 'M')
  ));

  const [countries, setCountries] = useState<Array<SelectData>>([
    { label: tInput("birthCountry.country.italy"), value: "IT" },
    { label: tInput("birthCountry.country.other"), value: "EE" },
  ]);
  const [selectedCountry, setSelectedCountry] = useState<SelectData | undefined>(countries.find(
    (country: SelectData) => country.value == (isNullOrEmpty(data?.birthPlace.country) ? 'IT' : data?.birthPlace.country)
  ));
  const [searchTermPlace, setSearchTermPlace] = useState<string>('');
  const [pagePlace, setPagePlace] = useState<number>(1);
  const [placeTotalCount, setPlaceTotalCount] = useState<number>(0);
  const [places, setPlaces] = useState<Array<SelectData>>([]);
  const [selectedPlace, setSelectedPlace] = useState<SelectData | undefined>(undefined);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // data di nascita
  const [selectedYear, setSelectedYear] = useState<SelectData>();
  const [filteredYears, setFilteredYears] = useState<Array<SelectData>>([]);

  const [filteredMonths, setFilteredMonths] = useState<Array<SelectData>>([]);
  const [selectedMonth, setSelectedMonth] = useState<SelectData>();

  const [days, setDays] = useState<Array<SelectData>>([]);
  const [filteredDays, setFilteredDays] = useState<Array<SelectData>>([]);
  const [selectedDay, setSelectedDay] = useState<SelectData>();

  const [years, setYears] = useState<Array<SelectData>>(() => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let y = currentYear - 15; y >= currentYear - 90; y--) {
      years.push({
        "value": y,
        "label": y.toString(),
      });
    }

    setFilteredYears(years);

    // settaggio eventuale valore iniziale
    if (data?.birthDate?.year) {
      const selectedValue: SelectData | undefined = years.find((value: SelectData) => {
        return value.value == data.birthDate.year
      })
      setSelectedYear(selectedValue);
    }

    return years;
  });

  const [months, setMonths] = useState<Array<SelectData>>(() => {
    const lang = locale == 'it' ? 'it' : 'en';
    const formatter = new Intl.DateTimeFormat(lang, { month: 'long' });

    const monthList: SelectData[] = Array.from({ length: 12 }, (_, index) => {
      const date = new Date(2000, index); // gennaio = 0
      const rawLabel: string = formatter.format(date);

      return {
        value: index + 1,
        label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
      };
    });

    setFilteredMonths(monthList);

    // settaggio eventuale valore iniziale
    if (data?.birthDate?.month) {
      const selectedValue: SelectData | undefined = monthList.find((value: SelectData) => {
        return value.value == data.birthDate.month
      })
      setSelectedMonth(selectedValue);
    }

    return monthList;
  });

  useEffect(() => {
    const daysInMonth = new Date((selectedYear?.value as number) ?? 2000, (selectedMonth?.value as number) ?? 0, 0).getDate();
    const days: SelectData[] = Array.from({ length: daysInMonth }, (_, i) => ({
      value: i + 1,
      label: (i + 1).toString(),
    }));

    // settaggio eventuale valore iniziale
    if (data?.birthDate?.day) {
      const selectedValue: SelectData | undefined = days.find((value: SelectData) => {
        return value.value == data.birthDate.day
      })
      setSelectedDay(selectedValue);
    }

    if (selectedDay && Number(selectedDay.value) > days.length) {
      setSelectedDay(undefined);
      onChangeData('', fiscalCodeDataName.birthDate.year);
      onChangeData('', fiscalCodeDataName.birthDate.month);
      onChangeData('', fiscalCodeDataName.birthDate.day);
    }

    setDays(days);
    setFilteredDays(days)
  }, [selectedYear, selectedMonth])
  // TODO check   }, [formData.birthDate.month, formData.birthDate.day, selectedYear, selectedMonth])


  const callGetPlaces = async (term: string) => {
    if (placeTotalCount && places.length >= placeTotalCount) {
      return;
    }

    setLoading(true);

    console.log("*** call get places...");

    let data;
    let res;
    const reqOptions = {
      page: pagePlace,
      perPage: 100,
      lang: locale,
      query: term,
    };
    if (!selectedCountry || selectedCountry.value == 'IT') {
      res = await fetchCities({
        country: 'italia',
        ...reqOptions,
      });
      data = res.response?.data || [];
    } else {
      res = await fetchCountries(reqOptions);
      data = res.response?.countries || [];
    }

    const valueTransformList: Array<SelectData> = [];
    data.forEach((value: StudentCountryDTO | CityDTO) => {
      valueTransformList.push({
        label: value.name!,
        value: value.code!,
      })
    });

    if (pagePlace === 1) {
      setPlaces(valueTransformList)
    } else {
      setPlaces((prev) => [...prev, ...valueTransformList]);
    }

    console.log("*** call get courses RES ", data);

    setPlaceTotalCount(res.response?.totalCount || 0);
    setLoading(false);
  };

  // useEffect(() => {
  //   if (firstName !== undefined) {
  //     setFormData(prev => ({
  //       ...prev,
  //       "firstName": firstName
  //     }));
  //   }
  // }, [firstName])

  // useEffect(() => {
  //   if (lastName !== undefined) {
  //     setFormData(prev => ({
  //       ...prev,
  //       "lastName": lastName
  //     }));
  //   }
  // }, [lastName])

  useEffect(() => {
    setPlaces([]);
    setPagePlace(1);
    setPlaceTotalCount(0);
    callGetPlaces("");
    setSelectedPlace(undefined);

    onChangeData('', fiscalCodeDataName.birthPlace.country);
    onChangeData('', fiscalCodeDataName.birthPlace.place);

  }, [selectedCountry])

  // Calculate the complete fiscal code
  const calculateFiscalCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors: any = {}

    // Extra validation
    let result: ValidationResult = validationNumber(selectedDay?.value, days[0].value as number, days[days.length - 1].value as number);
    if (!result.result) {
      newErrors['birthDate_day'] = result.message;
    }
    result = validationNumber(selectedMonth?.value, months[0].value as number, months[months.length - 1].value as number);
    if (!result.result) {
      newErrors['birthDate_month'] = result.message;
    }
    result = validationNumber(selectedYear?.value, years[years.length - 1].value as number, years[0].value as number);
    if (!result.result) {
      newErrors['birthDate_year'] = result.message;
    }

    if (!selectedCountry) {
      newErrors['birthPlace_country'] = tCommon("validation.error.valueMissing");
    }

    if (!selectedPlace) {
      newErrors['birthPlace_place'] = tCommon("validation.error.valueMissing");
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      return
    }

    const completeCode = await calculateCodiceFiscale({
      name: firstName,
      surname: lastName,
      gender: selectedGender!.value as Gender,
      birthDateDay: selectedDay!.value as number,
      birthDateMonth: selectedMonth!.value as number,
      birthDateYear: selectedYear!.value as number,
      birthplaceCountry: selectedCountry!.value as string,
      birthplace: selectedPlace!.value as string,
    })

    if (completeCode) {
      setFiscalCode(completeCode);
      setShowResult(true);
    }
  };

  useEffect(() => {
    setPagePlace(1)
  }, [])

  useEffect(() => {
    callGetPlaces(searchTermPlace);
  }, [pagePlace, searchTermPlace]);

  return (
    <div className="fixed inset-0 bg-blue-950/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-full overflow-y-auto">
        {showResult ? (
          <div className="py-3 px-6">
            <div className="text-center mb-6">
              <TitleHeading label={tFiscalCode('title')} size="h3" className="mb-2" />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-center">
                <TitleHeading label={fiscalCode} size="span" className="tracking-wider text-blue-800 mb-0" />
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                name="edit"
                variant="secondary"
                size="md"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowResult(false)
                }}
                className="col-span-1 w-full justify-center"
              >
                {tCta('edit')}
              </Button>
              <Button
                name="confirm"
                variant="primary"
                size="md"
                type="button"
                onClick={() => {
                  onConfirm(fiscalCode)
                }}
                className="col-span-1 w-full justify-center"
              >
                {tCta('confirm')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={calculateFiscalCode} >
            <div className="p-8">
              <div className="space-y-4">

                {/* Name */}
                <div>
                  <Input
                    id="firstName"
                    name="firstName"
                    label={tInput("name.label")}
                    placeholder={tInput("name.placeholder")}
                    minLength={2}
                    maxLength={128}
                    pattern={REGEX_TEXT_ONLY_CHAR}
                    required={true}
                    value={firstName}
                    onChange={(value: any) => {
                      setFirstName(value.target.value);
                      onChangeData(value.target.value, fiscalCodeDataName.firstName);
                    }} />
                </div>

                {/* Surname */}
                <div>
                  <Input
                    id="lastName"
                    name="lastName"
                    label={tInput("surname.label")}
                    placeholder={tInput("surname.placeholder")}
                    minLength={2}
                    maxLength={128}
                    pattern={REGEX_TEXT_ONLY_CHAR}
                    required={true}
                    value={lastName}
                    onChange={(value: any) => {
                      setLastName(value.target.value);
                      onChangeData(value.target.value, fiscalCodeDataName.lastName);
                    }} />
                </div>

                {/* Birth Date */}
                <div>
                  <Label label={tInput('birthday.label')} required={true} />
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      id={"birthDate.day"}
                      name={"birthDate.day"}
                      key={`birthDate.day-${selectedDay?.value ?? 'none'}`}
                      searchable={true}
                      loading={false}
                      searchPlaceholder={tInput("day.searchPlaceholder")}
                      placeholder={tInput("day.placeholder")}
                      onSearch={(e: SelectData) => {
                        const filtered = selectOnSeach(e, days, (e: SelectData) => {
                          setSelectedDay(e)
                          onChangeData(e.value, fiscalCodeDataName.birthDate.day);
                        });
                        setFilteredDays(filtered);
                      }}
                      onChange={(value: any) => {
                        setSelectedDay(value)
                        onChangeData(value.value, fiscalCodeDataName.birthDate.day);
                      }}
                      defaultValue={selectedDay}
                      selectFirstValue={false}
                      items={filteredDays}
                      idKey="value"
                      labelKey="label"
                      required={true}
                      error={errors['birthDate_day']}
                    />

                    <Select
                      id={"birthDate.month"}
                      name={"birthDate.month"}
                      key={`birthDate.month-${selectedMonth?.value ?? 'none'}`}
                      searchable={true}
                      loading={false}
                      searchPlaceholder={tInput("month.searchPlaceholder")}
                      placeholder={tInput("month.placeholder")}
                      onSearch={(e: SelectData) => {
                        const filtered = selectOnSeach(e, months, (e: SelectData) => {
                          setSelectedMonth(e)
                          onChangeData(e.value, fiscalCodeDataName.birthDate.month);
                        });
                        setFilteredMonths(filtered);
                      }}
                      onChange={(value: any) => {
                        setSelectedMonth(value)
                        onChangeData(value.value, fiscalCodeDataName.birthDate.month);
                      }}
                      defaultValue={selectedMonth}
                      selectFirstValue={false}
                      items={filteredMonths}
                      idKey="value"
                      labelKey="label"
                      required={true}
                      error={errors['birthDate_month']}
                    />

                    <Select
                      id={"birthDate.year"}
                      name={"birthDate.year"}
                      key={`birthDate.year-${selectedYear?.value ?? 'none'}`}
                      searchable={true}
                      loading={false}
                      searchPlaceholder={tInput("year.searchPlaceholder")}
                      placeholder={tInput("year.placeholder")}
                      onSearch={(e: SelectData) => {
                        const filtered = selectOnSeach(e, years, (e: SelectData) => {
                          setSelectedYear(e);
                          onChangeData(e.value, fiscalCodeDataName.birthDate.year);
                        });
                        setFilteredYears(filtered);
                      }}
                      onChange={(value: any) => {
                        setSelectedYear(value)
                        onChangeData(value.value, fiscalCodeDataName.birthDate.year);
                      }}
                      defaultValue={selectedYear}
                      selectFirstValue={false}
                      items={filteredYears}
                      idKey="value"
                      labelKey="label"
                      required={true}
                      error={errors['birthDate_year']}
                    />
                  </div>
                </div>

                {/* Birth Country */}
                <div className='label-fix'>
                  <Select
                    id={"birthCountry"}
                    name={"birthPlace.country"}
                    key={`birthPlace.country-${selectedCountry ? selectedCountry.value : 'none'}`}
                    searchable={false}
                    loading={false}
                    label={tInput("birthCountry.label")}
                    placeholder={tInput("birthCountry.placeholder")}
                    searchPlaceholder={tInput("birthCountry.searchPlaceholder")}
                    onChange={(value: any) => {
                      setSelectedCountry(value);
                      onChangeData(value.value, fiscalCodeDataName.birthPlace.country);
                    }}
                    items={countries}
                    defaultValue={selectedCountry}
                    idKey="value"
                    labelKey="label"
                    required={true}
                    className='select'
                    error={errors['birthPlace_country']}
                  />
                </div>

                {/* Birth Place */}
                <div>
                  <Select
                    id={"birthPlace"}
                    name={"birthPlace.place"}
                    key={`birthPlace.place-${selectedPlace ? selectedPlace.value : 'none'}`}
                    searchable={true}
                    loading={loading}
                    label={tInput("birthPlace.label")}
                    placeholder={tInput("birthPlace.placeholder")}
                    searchPlaceholder={tInput("birthPlace.searchPlaceholder")}
                    onSearch={(e) => {
                      setPagePlace(1);
                      setSearchTermPlace(e.toString());
                      setPlaceTotalCount(0);
                    }}
                    onScroll={() => {
                      setPagePlace((prevPage) => prevPage + 1);
                    }}
                    onChange={(value: SelectData) => {
                      setSelectedPlace(value);
                      onChangeData(value.value, fiscalCodeDataName.birthPlace.place);
                    }}
                    items={places}
                    defaultValue={selectedPlace}
                    idKey="value"
                    labelKey="label"
                    selectFirstValue={false}
                    required={true}
                    error={errors['birthPlace_place']}
                  />
                </div>

                {/* Gender */}
                <div>
                  <RadioButton
                    id="gender"
                    name="gender"
                    className=" gender-container"
                    onChange={(item) => {
                      setSelectedGender(item);
                      onChangeData(item.value, fiscalCodeDataName.gender);
                    }}
                    items={genders}
                    label={tInput('sex.label')}
                    required={true}
                    defaultValue={selectedGender}
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <Button
                  name="cancel"
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose()
                  }}
                  className="col-span-1 w-full justify-center"
                >
                  {tCta('cancel')}
                </Button>
                <Button
                  name="calc"
                  variant="primary"
                  size="md"
                  type='submit'
                  className="w-full justify-center"
                >
                  {tCta('calculate')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FiscalCodeModal;